import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { Button } from '@/components/button/Button'
import { SearchField } from '@/components/search-field/SearchField'
import { postResolveSharePointSite } from '@/user_files/external-storage/fetch/post-resolve-sharepoint-site'
import { postSearchSharePointSites } from '@/user_files/external-storage/fetch/post-search-sharepoint-sites'
import { getSharePointSiteErrorMessage, getSharePointSiteSearchErrorMessage } from '@/user_files/external-storage/sharepoint-site-error-message'
import { ExternalFileItem, SharePointSiteSearchResponse } from '@/user_files/external-storage/schemas'

interface AddSharePointSiteFormProps {
  accountId: string
  onSiteResolved: (site: ExternalFileItem) => void
}

const SEARCH_DEBOUNCE_MS = 350

/** Search by name, or resolve a pasted SharePoint URL through the same input. */
export function AddSharePointSiteForm({ accountId, onSiteResolved }: AddSharePointSiteFormProps) {
  const [input, setInput] = useState('')
  const [sites, setSites] = useState<SharePointSiteSearchResponse['sites']>([])
  const [hasSearched, setHasSearched] = useState(false)
  const [moreResultsAvailable, setMoreResultsAvailable] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [isResolving, setIsResolving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const resultRefs = useRef<(HTMLButtonElement | null)[]>([])
  const searchRequestIdRef = useRef(0)
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // A request may finish after the modal closes. Ignore it rather than reopening
  // the site on the next picker visit.
  const isMountedRef = useRef(true)
  const trimmedInput = input.trim()
  const looksLikeUrl =
    /^https?:\/\//i.test(trimmedInput) ||
    /^[^\s/]+\.sharepoint\.[^\s/]+(?:\/|$)/i.test(trimmedInput) ||
    /^[^\s/]+\.[^\s/]+\/(?:sites|teams)\//i.test(trimmedInput)
  const showAddSite = /[/.]/.test(trimmedInput)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Search hits come from an index that can be stale; resolve selected hits
  // against Graph again before browsing, just like pasted URLs.
  const openSiteUrl = async (url: string) => {
    if (!url || isResolving) return
    setIsResolving(true)
    setError(null)
    try {
      const site = await postResolveSharePointSite({ accountId, siteUrl: url })
      if (!isMountedRef.current) return
      setInput('')
      onSiteResolved(site)
    } catch (resolveError) {
      if (isMountedRef.current) setError(getSharePointSiteErrorMessage(resolveError))
    } finally {
      if (isMountedRef.current) setIsResolving(false)
    }
  }

  const searchSites = useCallback(
    async (query: string) => {
      const requestId = ++searchRequestIdRef.current
      setIsSearching(true)
      setError(null)
      setHasSearched(false)
      setSites([])
      try {
        const response = await postSearchSharePointSites({ accountId, query })
        if (!isMountedRef.current || searchRequestIdRef.current !== requestId) return
        setSites([...response.sites].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })))
        setMoreResultsAvailable(response.more_results_available)
        setHasSearched(true)
      } catch (searchFailure) {
        if (!isMountedRef.current || searchRequestIdRef.current !== requestId) return
        setError(getSharePointSiteSearchErrorMessage(searchFailure))
      } finally {
        if (isMountedRef.current && searchRequestIdRef.current === requestId) setIsSearching(false)
      }
    },
    [accountId]
  )

  useEffect(() => {
    if (!trimmedInput || looksLikeUrl) return

    const timer = setTimeout(() => {
      searchTimerRef.current = null
      void searchSites(trimmedInput)
    }, SEARCH_DEBOUNCE_MS)
    searchTimerRef.current = timer
    return () => {
      clearTimeout(timer)
      if (searchTimerRef.current === timer) searchTimerRef.current = null
    }
  }, [input, trimmedInput, looksLikeUrl, searchSites])

  const handleInputChange = (value: string) => {
    searchRequestIdRef.current += 1
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    setInput(value)
    setSites([])
    setHasSearched(false)
    setMoreResultsAvailable(false)
    setIsSearching(false)
    setError(null)
  }

  const handleAction = () => {
    if (!trimmedInput || isResolving) return
    if (looksLikeUrl) {
      void openSiteUrl(trimmedInput)
      return
    }
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    if (!isSearching) void searchSites(trimmedInput)
  }

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (sites.length > 0 && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault()
      resultRefs.current[event.key === 'ArrowDown' ? 0 : sites.length - 1]?.focus()
      return
    }
    if (event.key !== 'Enter') return
    event.preventDefault()
    handleAction()
  }

  const handleResultKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
    event.preventDefault()
    const nextIndex = event.key === 'ArrowDown' ? index + 1 : index - 1
    if (nextIndex < 0 || nextIndex >= sites.length) inputRef.current?.focus()
    else resultRefs.current[nextIndex]?.focus()
  }

  return (
    <div className="flex flex-col gap-2">
      <div>
        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
          <SearchField
            ref={inputRef}
            aria-label="Search SharePoint sites or paste a site URL"
            aria-invalid={!!error}
            {...(error ? { 'aria-describedby': 'sharepoint-site-input-error' } : {})}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            placeholder="Search or paste a site URL"
            className={`min-w-0 flex-1${error ? ' !border-red-600 focus-within:!border-red-600' : ''}`}
            disabled={isResolving}
            autoFocus
          />
          {showAddSite && (
            <Button
              type="button"
              onClick={() => void openSiteUrl(trimmedInput)}
              variant="filled"
              color="primary"
              className="w-full sm:w-auto"
              disabled={isResolving}
              loading={isResolving}
            >
              Add site
            </Button>
          )}
        </div>
        <p role="status" aria-atomic="true" className={`text-sm text-brand-neutral-600${isSearching || hasSearched ? ' mt-2' : ''}`}>
          {isSearching && 'Searching sites…'}
          {hasSearched && sites.length === 0 && 'No matching sites found. Try another name or paste a site URL.'}
          {hasSearched && sites.length > 0 && `${sites.length} site${sites.length === 1 ? '' : 's'} found.`}
          {hasSearched && moreResultsAvailable && ' More sites may match. Try a more specific name.'}
        </p>
      </div>
      {error && (
        <p id="sharepoint-site-input-error" role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}
      {sites.length > 0 && (
        <div className="max-h-52 overflow-y-auto rounded border border-brand-neutral-200">
          {sites.map((site, index) => (
            <button
              key={site.id}
              ref={(node) => {
                resultRefs.current[index] = node
              }}
              type="button"
              disabled={isResolving}
              onClick={() => void openSiteUrl(site.web_url)}
              onKeyDown={(event) => handleResultKeyDown(event, index)}
              className="flex w-full min-w-0 flex-col border-b border-brand-neutral-200 px-3 py-2 text-left last:border-b-0 hover:bg-brand-neutral-100 disabled:opacity-50"
              aria-label={`Open ${site.name}`}
            >
              <span className="text-sm font-medium text-brand-neutral-950">{site.name}</span>
              <span className="break-all text-xs text-brand-neutral-600">{site.web_url}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
